import { Button, Checkbox, Toggle } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type ExtensionResponse, listTeamsOptions } from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { PerUserSignIn } from "./IntegrationSections";
import type { IntegrationActions } from "./useIntegrationActions";

/** Who can use it: no teams picked means everyone. */
export function TeamAccessEditor({
  extension,
  actions,
}: Readonly<{ extension: ExtensionResponse; actions: IntegrationActions }>) {
  const { t } = useTranslation("adminIntegrations");
  const teams = useQuery(listTeamsOptions({ query: { page_size: 100 } }));
  const saved = (extension.teamAccess ?? []).map((a) => a.teamUuid);
  const [picked, setPicked] = useState<string[]>(saved);
  const choices = (teams.data?.data ?? []).filter((team) => !team.isEveryone);
  const changed = picked.length !== saved.length || picked.some((uuid) => !saved.includes(uuid));

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1">
        <Text as="span" variant="body">
          {t("edit.limitTo")}
        </Text>
      </legend>
      <Text variant="caption" tone="tertiary">
        {picked.length === 0 ? t("edit.everyoneHint") : t("edit.teamsHint", { count: picked.length })}
      </Text>
      {teams.isSuccess && choices.length === 0 && (
        <Text variant="body-sm" tone="tertiary">
          {t("edit.noTeams")}
        </Text>
      )}
      {choices.map((team) => (
        <Checkbox
          key={team.uuid}
          label={team.name}
          checked={picked.includes(team.uuid)}
          onChange={(on) => setPicked(on ? [...picked, team.uuid] : picked.filter((id) => id !== team.uuid))}
        />
      ))}
      {changed && (
        <div className="flex gap-2 pt-2">
          <Button variant="secondary" size="sm" onClick={() => setPicked(saved)} disabled={actions.savingTeams}>
            {t("edit.cancel")}
          </Button>
          <Button variant="primary" size="sm" loading={actions.savingTeams} onClick={() => actions.setTeams(picked)}>
            {t("edit.saveAccess")}
          </Button>
        </div>
      )}
    </fieldset>
  );
}

/** On/off. Turning on waits until setup is complete; turning off is always allowed. */
export function EnabledToggle({
  extension,
  actions,
}: Readonly<{ extension: ExtensionResponse; actions: IntegrationActions }>) {
  const { t } = useTranslation("adminIntegrations");
  const state = extension.setup?.state;
  const blocked = !extension.isEnabled && (state === "needs_setup" || state === "unavailable");
  return (
    <div className="flex flex-col items-start gap-1 md:items-end">
      <Toggle
        label={t("edit.enabled")}
        checked={extension.isEnabled}
        disabled={blocked || actions.toggling}
        onChange={actions.setEnabled}
      />
      {blocked && (
        <Text variant="caption" tone="tertiary">
          {t(state === "unavailable" ? "edit.cantTurnOnUnavailable" : "edit.cantTurnOn")}
        </Text>
      )}
    </div>
  );
}

export function AccessEditSection({
  extension,
  actions,
}: Readonly<{ extension: ExtensionResponse; actions: IntegrationActions }>) {
  const { t } = useTranslation("adminIntegrations");
  return (
    <PageSection title={t("detail.access")}>
      <TeamAccessEditor
        key={(extension.teamAccess ?? []).map((a) => a.teamUuid).join()}
        extension={extension}
        actions={actions}
      />
      <PerUserSignIn extension={extension} />
    </PageSection>
  );
}
